import React from 'react';
import styled from 'styled-components';
import { portcoTokens } from './PortcoTheme';

/**
 * Shared modal chrome for portco lifecycle and mark dialogs. Slate-on-paper,
 * institutional finance — no rounded pills, no glass, single navy accent.
 * Pairs with PortcoTheme tokens.
 */

export const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(11, 30, 58, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 64px 16px 32px;
  z-index: 1000;
`;

export const ModalCard = styled.div`
  width: 100%;
  max-width: 520px;
  background: ${portcoTokens.surfaceBody};
  border: 1px solid ${portcoTokens.hairlineStrong};
  box-shadow: 0 24px 48px -16px rgba(11, 30, 58, 0.35);
  font-family: ${portcoTokens.fontSans};
  color: ${portcoTokens.inkBody};
`;

export const ModalHeader = styled.div`
  background: ${portcoTokens.surfaceHeader};
  color: ${portcoTokens.inkOnDark};
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
`;

export const ModalTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const ModalTitleText = styled.div`
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
`;

export const ModalSubtitle = styled.div`
  font-size: 11px;
  color: ${portcoTokens.inkOnDarkMuted};
  letter-spacing: 0.04em;
`;

export const ModalCloseBtn = styled.button`
  background: transparent;
  border: none;
  color: ${portcoTokens.inkOnDarkMuted};
  font-size: 18px;
  line-height: 1;
  cursor: pointer;
  padding: 0;
  &:hover { color: ${portcoTokens.inkOnDark}; }
`;

export const ModalBody = styled.div`
  padding: 18px 20px 8px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const FormGrid = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$cols ?? 2}, minmax(0, 1fr));
  gap: 12px 14px;
`;

export const FormField = styled.div<{ $span?: number }>`
  display: flex;
  flex-direction: column;
  gap: 4px;
  grid-column: span ${(p) => p.$span ?? 1};
`;

export const FieldLabel = styled.label`
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${portcoTokens.inkMuted};
`;

export const TextInput = styled.input`
  font: inherit;
  font-size: 13px;
  background: #fff;
  border: 1px solid ${portcoTokens.hairline};
  padding: 6px 8px;
  color: ${portcoTokens.inkBody};
  &:focus {
    outline: none;
    border-color: ${portcoTokens.accent};
  }
`;

export const SelectInput = styled.select`
  font: inherit;
  font-size: 13px;
  background: #fff;
  border: 1px solid ${portcoTokens.hairline};
  padding: 6px 8px;
  color: ${portcoTokens.inkBody};
  &:focus {
    outline: none;
    border-color: ${portcoTokens.accent};
  }
`;

export const ContextStrip = styled.div`
  border: 1px solid ${portcoTokens.hairline};
  background: ${portcoTokens.surfaceMuted};
  padding: 10px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: ${portcoTokens.inkMuted};
  font-family: ${portcoTokens.fontMono};
`;

export const ErrorBanner = styled.div`
  background: ${portcoTokens.statusBreachedSubtle};
  border: 1px solid ${portcoTokens.statusBreached};
  color: ${portcoTokens.statusBreached};
  padding: 8px 10px;
  font-size: 12px;
`;

export const ModalFoot = styled.div`
  border-top: 1px solid ${portcoTokens.hairline};
  padding: 12px 20px;
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`;

export const PrimaryBtn = styled.button`
  background: ${portcoTokens.inkPrimary};
  border: 1px solid ${portcoTokens.inkPrimary};
  color: #fff;
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 14px;
  cursor: pointer;
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

export const SecondaryBtn = styled.button`
  background: transparent;
  border: 1px solid ${portcoTokens.hairline};
  color: ${portcoTokens.inkBody};
  font-size: 11px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 6px 14px;
  cursor: pointer;
`;

interface ShellProps {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
}

export const PortcoModalShell: React.FC<ShellProps> = ({ title, subtitle, onClose, children }) => (
  <Backdrop onClick={onClose}>
    <ModalCard onClick={(e) => e.stopPropagation()}>
      <ModalHeader>
        <ModalTitle>
          <ModalTitleText>{title}</ModalTitleText>
          {subtitle ? <ModalSubtitle>{subtitle}</ModalSubtitle> : null}
        </ModalTitle>
        <ModalCloseBtn onClick={onClose} aria-label="Close">×</ModalCloseBtn>
      </ModalHeader>
      {children}
    </ModalCard>
  </Backdrop>
);
